import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import type { InvoiceGroup } from "@shared-core/data/supplier-portal-adapter";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  INVOICE_WORDING,
  STAFF_VIEW_WORDING,
  dayMonth,
  invoiceGroupLabel,
  invoiceStatus,
  platformToday,
  pounds,
} from "@shared-core/domain/supplier-portal-wording";
import { Card } from "../../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { cn } from "../../app/components/primitives/utils";
import { InvoiceUploadForm } from "../../app/components/domain/InvoiceUploadForm";
import { StatusPill } from "../../app/components/domain/StatusPill";
import {
  StaffViewReadOnly,
  StaffViewReadOnlyNoteOnce,
} from "../../app/components/domain/StaffViewReadOnly";
import { useIsStaffView } from "../shell/staffView";
import { useSupplierSession } from "../shell/session";
import { EmptyState, LoadFailed, LoadingCard } from "../shell/states";
import { InvoiceQueryCard } from "../../app/components/domain/InvoiceQueryCard";

const LOGIN = { via: "login" } as const;
const GROUPS: InvoiceGroup[] = ["in_progress", "paid"];

/**
 * `/invoices` (login only): the supplier's invoices with their status from our ledger (received, approved with
 * due date, paid with date, or a query from Pollards), a finance query answered beside its invoice, and an upload
 * against one or more purchase orders. Uploads go into the purchase invoice intake finance already uses (EA §5.7).
 */
export function InvoicesPage() {
  const adapter = useSupplierPortalAdapter();
  const session = useSupplierSession();
  const isStaffView = useIsStaffView();
  const [params] = useSearchParams();
  const group: InvoiceGroup =
    params.get("group") === "paid" ? "paid" : "in_progress";
  const invoices = useLoad(() => adapter.listInvoices(LOGIN, { group }), group);
  // The staff view draws the form switched off with no orders to choose: the upload options need the supplier's own
  // permission to upload, so the staff routes do not mirror them (docs/features/supplier-portal-view-as/05 §12).
  const options = useLoad(
    () =>
      isStaffView
        ? Promise.resolve({ purchase_order_nos: [], today: platformToday() })
        : adapter.getInvoiceUploadOptions(LOGIN),
    "upload-options",
  );
  const queries = invoices.data?.items.filter((i) => i.query?.can_reply) ?? [];
  const [isReplied, setIsReplied] = useState(false);

  const refresh = () => {
    invoices.reload();
    options.reload();
    session?.refresh();
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18.75rem]">
      <div className="grid min-w-0 content-start gap-3">
        <h1 className="sr-only">Invoices</h1>
        <nav aria-label="Invoice status" className="flex gap-1">
          {GROUPS.map((g) => (
            <Link
              key={g}
              to={
                g === "in_progress"
                  ? paths.supplierInvoices()
                  : `${paths.supplierInvoices()}?group=paid`
              }
              aria-current={g === group ? "page" : undefined}
              className={cn(
                "border-b-2 px-2.5 py-2 text-sm",
                g === group
                  ? "border-primary font-semibold text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {invoiceGroupLabel(g)}
            </Link>
          ))}
        </nav>

        <StaffViewReadOnlyNoteOnce sentence={STAFF_VIEW_WORDING.invoices} />

        {invoices.data ? (
          invoices.data.items.length === 0 ? (
            <Card>
              <EmptyState title={INVOICE_WORDING.empty(group)} />
            </Card>
          ) : (
            <Card className="gap-0 overflow-x-auto py-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Your invoice</TableHead>
                    <TableHead>For</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.data.items.map((invoice) => (
                    <TableRow key={invoice.invoice_ref}>
                      <TableCell className="font-mono text-xs">
                        {invoice.invoice_ref}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {invoice.purchase_order_nos.length
                          ? invoice.purchase_order_nos.join(", ")
                          : INVOICE_WORDING.noPurchaseOrder}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {pounds(invoice.total_inc_vat)}
                      </TableCell>
                      <TableCell>{dayMonth(invoice.received_on)}</TableCell>
                      <TableCell>
                        <StatusPill {...invoiceStatus(invoice)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )
        ) : invoices.error ? (
          <LoadFailed
            what="your invoices"
            message={mutationErrorMessage(
              invoices.error,
              "Something went wrong reaching Pollards.",
            )}
            onRetry={invoices.reload}
          />
        ) : (
          <LoadingCard what="your invoices" />
        )}

        {isReplied && (
          <p role="status" className="text-sm font-semibold text-green-700">
            ✓ {INVOICE_WORDING.replied}
          </p>
        )}
        {queries.map((invoice) => (
          <InvoiceQueryCard
            key={invoice.invoice_ref}
            invoice={invoice}
            onReply={async (input) => {
              await adapter.replyToInvoiceQuery(
                LOGIN,
                invoice.invoice_ref,
                input,
              );
              setIsReplied(true);
              refresh();
            }}
          />
        ))}
      </div>

      <Card className="content-start gap-3 self-start p-4">
        <h2 className="font-semibold">{INVOICE_WORDING.uploadTitle}</h2>
        {options.data ? (
          <StaffViewReadOnly>
            <InvoiceUploadForm
              key={options.data.purchase_order_nos.join(",")}
              choices={options.data.purchase_order_nos}
              today={options.data.today}
              onSubmit={(input) => adapter.uploadInvoice(LOGIN, input)}
              onUploaded={refresh}
            />
          </StaffViewReadOnly>
        ) : options.error ? (
          <p role="alert" className="text-sm text-red-600">
            {mutationErrorMessage(
              options.error,
              "We couldn't load the upload form.",
            )}
          </p>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Loading…
          </p>
        )}
      </Card>
    </div>
  );
}
